"use client";

import { useState, useRef, useCallback } from "react";
import Link from "next/link";
import {
  HiUpload,
  HiQuestionMarkCircle,
  HiCheckCircle,
  HiXCircle,
  HiRefresh,
  HiLightBulb,
} from "@/components/icons";
import { FaChrome } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";

const CWS_MAX_BYTES = 10 * 1024 * 1024; // 10MB
const WARN_BYTES = 5 * 1024 * 1024; // 5MB warning threshold

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

interface FileInfo {
  name: string;
  size: number;
  type: string;
  lastModified: number;
}

interface AnalysisResult {
  fileName: string;
  totalSize: number;
  passes: boolean;
  warns: boolean;
  percentage: number;
}

const faqItems = [
  {
    question: "What is the Chrome Web Store size limit?",
    answer:
      "The Chrome Web Store enforces a 10MB maximum size for extension ZIP files. If your extension exceeds this limit, your upload will be rejected. This tool checks your ZIP file against that limit before you attempt to submit, saving you time during the publishing process.",
  },
  {
    question: "How can I reduce my extension's file size?",
    answer:
      "Common ways to reduce extension size: compress images (use WebP instead of PNG/JPG), remove unused node_modules and devDependencies from any bundled code, minify your JavaScript and CSS files, remove source maps from production builds, exclude test files and documentation, and use tree-shaking in your bundler (webpack, esbuild, Rollup) to eliminate dead code.",
  },
  {
    question: "Does this tool send my file to a server?",
    answer:
      "No. Everything is processed entirely in your browser using the browser's built-in File API. Your extension ZIP file never leaves your computer. This tool reads the file locally and calculates its size without any network requests.",
  },
  {
    question: "Should I include node_modules in my extension ZIP?",
    answer:
      "No — never include node_modules in your published extension. Use a bundler like webpack, esbuild, or Rollup to bundle only the code your extension actually uses into a single file (or a small set of files). A raw node_modules folder can be hundreds of megabytes, far exceeding the 10MB limit.",
  },
  {
    question: "What file types count the most toward the size limit?",
    answer:
      "Images (PNG, JPG, GIF) and unminified JavaScript are typically the biggest culprits. Switching to WebP images, removing screenshots from the ZIP (they belong in your Chrome Web Store listing, not the extension package), minifying JS/CSS, and removing source maps are the highest-impact optimizations.",
  },
];

export default function ChromeExtensionSizeCheckerClient() {
  const [isDragging, setIsDragging] = useState(false);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback((file: File) => {
    setError(null);
    setResult(null);

    if (
      !file.name.toLowerCase().endsWith(".zip") &&
      file.type !== "application/zip" &&
      file.type !== "application/x-zip-compressed"
    ) {
      setError("Please upload a ZIP file (.zip).");
      return;
    }

    const info: FileInfo = {
      name: file.name,
      size: file.size,
      type: file.type || "application/zip",
      lastModified: file.lastModified,
    };
    setFileInfo(info);

    const passes = file.size <= CWS_MAX_BYTES;
    const warns = file.size > WARN_BYTES && file.size <= CWS_MAX_BYTES;
    const percentage = Math.min((file.size / CWS_MAX_BYTES) * 100, 100);

    setResult({
      fileName: file.name,
      totalSize: file.size,
      passes,
      warns,
      percentage,
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const reset = () => {
    setFileInfo(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const barColor = result
    ? result.passes
      ? result.warns
        ? "bg-yellow-500"
        : "bg-green-500"
      : "bg-red-500"
    : "bg-primary";

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Chrome Extension Size Checker"
        description="Upload your Chrome extension ZIP file and instantly check if it meets the Chrome Web Store's 10MB size limit."
        url="https://plugmonkey.xyz/tool/chrome-extension-size-checker/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Chrome Extension Size Checker",
            url: "https://plugmonkey.xyz/tool/chrome-extension-size-checker/",
          },
        ]}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-blue-600/10 rounded-full text-blue-600">
          <FaChrome className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Chrome Extension Size Checker</h1>
          <p className="text-muted-foreground mt-1">
            Verify your extension ZIP is under the Chrome Web Store's 10MB limit before submitting.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          The Chrome Web Store enforces a strict 10MB size limit on extension ZIP files. If your
          package exceeds this, your upload will be rejected — often without a clear error message.
          This tool lets you check your extension size locally before you waste time submitting.
        </p>
        <p>
          Your file is analyzed entirely in your browser using the File API. No data is uploaded to
          any server. Just drop your ZIP and get an instant pass/fail result.
        </p>
      </div>

      {/* Drop Zone */}
      {!result && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors mb-8 ${
            isDragging
              ? "border-primary bg-primary/10"
              : "border-border hover:border-primary/60 hover:bg-primary/5"
          }`}
        >
          <input
            id="extension-zip"
            name="extension-zip"
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            className="hidden"
            onChange={handleFileChange}
          />
          <HiUpload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-semibold mb-1">Drop your extension ZIP here</p>
          <p className="text-sm text-muted-foreground">or click to browse files</p>
          <p className="text-xs text-muted-foreground mt-3">Accepts .zip files only</p>
        </div>
      )}

      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-4 mb-6 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Result */}
      {result && fileInfo && (
        <div className="space-y-6 mb-8">
          <Card
            className={
              result.passes
                ? result.warns
                  ? "border-yellow-400 bg-yellow-50/50 dark:bg-yellow-950/20"
                  : "border-green-400 bg-green-50/50 dark:bg-green-950/20"
                : "border-red-400 bg-red-50/50 dark:bg-red-950/20"
            }
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                {result.passes ? (
                  <HiCheckCircle
                    className={`w-7 h-7 ${result.warns ? "text-yellow-500" : "text-green-500"}`}
                  />
                ) : (
                  <HiXCircle className="w-7 h-7 text-red-500" />
                )}
                <span>
                  {result.passes
                    ? result.warns
                      ? "Passes — but getting large"
                      : "Passes the 10MB limit"
                    : "Exceeds the 10MB limit"}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">File name</p>
                  <p className="font-semibold truncate">{result.fileName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total size</p>
                  <p className="font-semibold text-lg">{formatBytes(result.totalSize)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">CWS limit</p>
                  <p className="font-semibold">10.00 MB</p>
                </div>
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>0 MB</span>
                  <span>{result.percentage.toFixed(1)}% of limit used</span>
                  <span>10 MB</span>
                </div>
                <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all ${barColor}`}
                    style={{ width: `${result.percentage}%` }}
                  />
                </div>
              </div>

              {!result.passes && (
                <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                  Your extension is {formatBytes(result.totalSize - CWS_MAX_BYTES)} over the limit.
                  See optimization tips below.
                </p>
              )}
              {result.warns && result.passes && (
                <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                  Your extension is over 50% of the limit. Consider optimizing before adding more
                  features.
                </p>
              )}

              <Button variant="outline" size="sm" onClick={reset}>
                <HiRefresh className="mr-2 w-4 h-4" /> Check another file
              </Button>
            </CardContent>
          </Card>

          {/* Optimization Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HiLightBulb className="w-5 h-5 text-yellow-500" />
                Optimization Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">1.</span>
                  <span>
                    <strong className="text-foreground">Remove node_modules</strong> — never include
                    them in your ZIP. Bundle only what you use with webpack, esbuild, or Rollup.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">2.</span>
                  <span>
                    <strong className="text-foreground">Compress images</strong> — convert PNG/JPG
                    to WebP and run them through a compressor like Squoosh or TinyPNG.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">3.</span>
                  <span>
                    <strong className="text-foreground">Remove source maps</strong> — .map files add
                    significant size and should not be shipped in production builds.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">4.</span>
                  <span>
                    <strong className="text-foreground">Minify JS and CSS</strong> — production
                    bundles should be minified. Unminified code is often 2–3x larger.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">5.</span>
                  <span>
                    <strong className="text-foreground">Remove screenshots from ZIP</strong> —
                    promotional images belong in your CWS listing, not inside the extension package.
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <div className="bg-card border rounded-xl p-6">
            <h3 className="font-bold text-lg mb-3">Need a Chrome Extension Built?</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Whether you need a lightweight productivity tool or a powerful data extraction
              extension, <strong>PlugMonkey</strong> builds custom Chrome extensions for businesses
              and individuals.
            </p>
            <Button asChild variant="secondary">
              <Link href="/custom-extension-quote/">Get a Quote</Link>
            </Button>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={faqItems} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
